import { z } from "zod";
import { Address } from "./mail.ts";

// Compose and send. Recipient rules live here so the composer and the API agree, and they are unit-tested once.

export const ComposeMode = z.enum(["new", "reply", "replyAll", "forward"]);

export const Draft = z.object({
  id: z.number().int(),
  mode: ComposeMode,
  /** Message being replied to or forwarded. */
  refMessageId: z.number().int().nullable(),
  from: z.string(),
  to: z.array(Address),
  cc: z.array(Address),
  bcc: z.array(Address),
  subject: z.string(),
  html: z.string(),
  /** Quoted original, kept apart from the body so it can stay collapsed and is never edited by accident. */
  quoteHtml: z.string(),
  signatureId: z.number().int().nullable(),
  inReplyTo: z.string().nullable(),
  references: z.array(z.string()),
  attachments: z.array(z.object({ id: z.string(), name: z.string(), mime: z.string(), size: z.number().int() })),
  updatedAt: z.string(),
});

export const DraftInput = Draft.omit({ id: true, updatedAt: true, attachments: true }).extend({
  attachmentIds: z.array(z.string().uuid()).max(50),
}).partial();

export const Signature = z.object({
  id: z.number().int(),
  name: z.string().min(1).max(80),
  html: z.string().max(100_000),
  defaultNew: z.boolean(),
  defaultReply: z.boolean(),
});
export const SignatureInput = Signature.omit({ id: true });

export const ReplyContext = z.object({
  messageId: z.string().nullable(),
  references: z.array(z.string()),
  from: Address,
  replyTo: z.array(Address),
  to: z.array(Address),
  cc: z.array(Address),
  subject: z.string(),
  date: z.string(),
  quoteHtml: z.string(),
  attachments: z.array(z.object({ index: z.number(), filename: z.string(), contentType: z.string(), size: z.number() })),
});

export const Outbox = z.object({
  id: z.number().int(),
  draftId: z.number().int().nullable(),
  subject: z.string(),
  to: z.array(z.string()),
  sendAt: z.string(),
  status: z.enum(["queued", "sending", "sent", "failed", "cancelled"]),
  error: z.string().nullable(),
});

/** Largest single attachment, and the total a message may carry (mailcow accepts 100 MB, receivers mostly 25). */
export const ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;

export type ComposeMode = z.infer<typeof ComposeMode>;
export type Draft = z.infer<typeof Draft>;
export type DraftInput = z.infer<typeof DraftInput>;
export type Signature = z.infer<typeof Signature>;
export type SignatureInput = z.infer<typeof SignatureInput>;
export type ReplyContext = z.infer<typeof ReplyContext>;
export type Outbox = z.infer<typeof Outbox>;

type Addr = { name: string | null; email: string };

const norm = (e: string) => e.trim().toLowerCase();

function uniq(list: Addr[], skip: Set<string>): Addr[] {
  const seen = new Set(skip);
  const out: Addr[] = [];
  for (const a of list) {
    const k = norm(a.email);
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push({ name: a.name, email: a.email.trim() });
  }
  return out;
}

/**
 * Who a reply goes to. Reply: the Reply-To if set, else the sender. Reply all: that plus everyone else on To and
 * Cc, keeping Cc people on Cc. Never you or your own addresses. Replying to your own sent message goes to its
 * original recipients, the way Gmail does.
 */
export function replyRecipients(
  original: { from: Addr; replyTo: Addr[]; to: Addr[]; cc: Addr[] },
  mode: "reply" | "replyAll",
  mine: string[],
): { to: Addr[]; cc: Addr[] } {
  const me = new Set(mine.map(norm));
  const fromMe = me.has(norm(original.from.email));
  const primary = fromMe ? original.to : original.replyTo.length ? original.replyTo : [original.from];
  const to = uniq(primary, me);
  if (mode === "reply") return { to: to.length ? to : uniq(original.to, new Set()), cc: [] };
  const others = fromMe ? [] : original.to;
  const toAll = uniq([...primary, ...others], me);
  const cc = uniq(original.cc, new Set([...me, ...toAll.map((a) => norm(a.email))]));
  return { to: toAll, cc };
}

export function domainOf(email: string): string {
  return norm(email).split("@")[1] ?? "";
}

/** Recipients outside your organisation, for the composer's warning. */
export function externalRecipients(list: Addr[], myDomain: string): Addr[] {
  return list.filter((a) => domainOf(a.email) !== norm(myDomain));
}

/** "Re: " once, never "Re: Re: ". Forward gets "Fwd: ". */
export function replySubject(subject: string, mode: "reply" | "replyAll" | "forward"): string {
  const base = subject.replace(/^(\s*((re|fw|fwd|aw|wg|sv|vs)(\[\d+\])?\s*:\s*))+/i, "").trim();
  return `${mode === "forward" ? "Fwd" : "Re"}: ${base}`;
}

/** References for a reply: the original's chain plus its own Message-ID, capped like Thunderbird does. */
export function replyReferences(original: { messageId: string | null; references: string[] }): string[] {
  const chain = [...original.references, ...(original.messageId ? [original.messageId] : [])].filter((v, i, a) => v && a.indexOf(v) === i);
  return chain.length > 20 ? [chain[0]!, ...chain.slice(-19)] : chain;
}

/** "Ali <ali@x.com>, b@y.com" -> addresses; anything without an @ is dropped. */
export function parseAddressList(input: string): Addr[] {
  return input
    .split(/[,;\n]/)
    .map((part) => {
      const m = part.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/);
      const email = (m?.[2] ?? part).trim();
      return { name: m && m[1]?.trim() ? m[1].trim() : null, email };
    })
    .filter((a) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email));
}
